import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Package,
  Camera,
  BellRinging,
  ShieldCheck,
  FileText,
  WifiSlash,
  CheckCircle,
  Star,
} from "@phosphor-icons/react";
import BrandMark from "../components/BrandMark";
import { api } from "../lib/api";

const TIER_COPY = {
  basic: {
    tagline: "For solo operators & one-person shops.",
    bullets: [
      "Unlimited parts & SKUs",
      "Real-time stock + full audit trail",
      "Low-stock push & email alerts",
      "Role-based access (Admin / Viewer)",
      "CSV & PDF inventory reports",
      "2 users included (1 admin + 1 viewer)",
    ],
  },
  team: {
    tagline: "For growing shops that need warehouse staff.",
    bullets: [
      "Everything in Basic, plus:",
      "Camera barcode + QR scanning (PWA)",
      "Up to 5 users — Admin / Staff / Viewer",
      "Warehouse Staff role (logs IN/OUT, edits stock)",
      "End-of-day digest email summaries",
      "Priority push alerts to all admins + staff",
      "Per-user audit trail for every movement",
    ],
  },
  pro: {
    tagline: "For multi-team operations & accounting needs.",
    bullets: [
      "Everything in Team, plus:",
      "Up to 10 users — all roles",
      "Financial reports (margin, COGS, valuation)",
      "PDF/CSV financial exports for accounting",
      "Priority email support",
    ],
  },
};
const TIER_ORDER = ["basic", "team", "pro"];

const FEATURES = [
  {
    icon: Package,
    title: "Real-time stock counts",
    body:
      "Track every SKU, part number, and location with live IN / OUT / adjustment movements. Every change is audited and attributable to the operator who made it.",
  },
  {
    icon: Camera,
    title: "Camera barcode + QR scanning",
    body:
      "Point your phone at a label and the SKU populates instantly — no hardware scanner required. Torch toggle for poorly lit warehouses.",
  },
  {
    icon: BellRinging,
    title: "Low-stock push alerts",
    body:
      "Get a push notification the moment a part crosses its reorder threshold or hits zero. Admins also see every IN / OUT in real time.",
  },
  {
    icon: ShieldCheck,
    title: "Role-based access",
    body:
      "Three tiers: Admin manages users, parts, and categories; Warehouse Staff handles stock movements; Viewer sees stock levels only — never costs.",
  },
  {
    icon: FileText,
    title: "CSV & PDF reporting",
    body:
      "Export inventory snapshots, transaction history, and financial reports straight from the app. Branded PDFs ready for accounting or audits.",
  },
  {
    icon: WifiSlash,
    title: "Works offline. Installs like an app.",
    body:
      "ezItemizer is a Progressive Web App — install it to a phone home screen and the shell keeps working through flaky wifi on the shop floor.",
  },
];

const STATS = [
  { value: "Unlimited", label: "Parts SKUs" },
  { value: "3 Tiers", label: "Access Roles" },
  { value: "Full", label: "Audit Trail" },
  { value: "PWA", label: "Mobile + Offline" },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/billing/config")
      .then((r) => {
        if (cancelled) return;
        const list = (r.data?.plans || []).filter((p) => p.payment_link);
        // Order: basic, team, pro
        list.sort(
          (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)
        );
        setPlans(list);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0F172A] font-sans">
      {/* ===== Top navigation ===== */}
      <header className="absolute top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 text-white"
            data-testid="landing-brand-link"
          >
            <BrandMark size={40} className="flex-shrink-0 rounded-md" />
            <span className="font-display text-lg sm:text-xl font-bold tracking-tight">
              ezItemizer
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm text-white/75">
            <a href="#features" className="hover:text-white transition-colors">
              Features
            </a>
            <a href="#how-it-works" className="hover:text-white transition-colors">
              How it works
            </a>
            <a href="#pricing" className="hover:text-white transition-colors">
              Pricing
            </a>
            <Link to="/faq" data-testid="landing-nav-faq" className="hover:text-white transition-colors">
              FAQ
            </Link>
            <Link to="/contact" data-testid="landing-nav-contact" className="hover:text-white transition-colors">
              Contact
            </Link>
            <Link to="/login" className="hover:text-white transition-colors">
              Sign in
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/login"
              data-testid="landing-mobile-signin"
              className="md:hidden text-white/85 hover:text-white text-xs font-bold uppercase tracking-widest"
            >
              Sign in
            </Link>
            <a
              href="#pricing"
              data-testid="landing-getstarted-button"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] text-xs sm:text-sm font-bold uppercase tracking-widest px-4 py-2.5 sm:px-5 sm:py-3 transition-colors"
            >
              Start free trial
              <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative bg-[#1A2332] text-white overflow-hidden pt-28 sm:pt-36 pb-24 sm:pb-32">
        {/* Backdrop accents */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#FF9800 1px, transparent 1px), linear-gradient(90deg, #FF9800 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF9800] opacity-10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Left column */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#FF9800]/40 bg-[#FF9800]/10 px-3 py-1.5">
              <span className="w-1.5 h-1.5 bg-[#FF9800] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
                Built for warehouses · SMBs · job sites
              </span>
            </div>

            <h1 className="font-display font-black tracking-tight mt-6 text-[44px] leading-[1.05] sm:text-6xl lg:text-7xl">
              Inventory that
              <br />
              <span className="text-[#FF9800]">survives</span> the shop floor.
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-white/75 leading-relaxed max-w-xl">
              Live stock counts, role-aware access, push alerts the moment a part
              hits its reorder line, and a full audit trail for every movement.
              Works on shop tablets, phones, and computers.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#pricing"
                data-testid="hero-cta-signup"
                className="inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] text-xs sm:text-sm font-bold uppercase tracking-widest px-6 py-4 transition-colors"
              >
                Start 14-day free trial
                <ArrowRight size={16} weight="bold" />
              </a>
              <a
                href="#pricing"
                className="text-xs sm:text-sm font-bold uppercase tracking-widest text-white/70 hover:text-white border-b border-white/30 hover:border-white pb-1 transition-colors"
              >
                See pricing →
              </a>
            </div>

            {/* Trust strip */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.label} className="bg-[#1A2332] px-4 py-4">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-[#FF9800]">
                    {s.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — product preview card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-4 bg-[#FF9800]/20 blur-2xl pointer-events-none"
                aria-hidden="true"
              />
              <div className="relative bg-white text-[#0F172A] border border-white/20 shadow-2xl">
                <div className="bg-[#0F172A] text-white px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FF9800]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest">
                      ezItemizer · live stock
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-white/50">
                    {new Date().toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>

                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between border-b border-zinc-200 pb-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Total Parts
                      </div>
                      <div className="font-mono font-bold text-3xl mt-1">
                        1,284
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                        Inventory Value
                      </div>
                      <div className="font-mono font-bold text-3xl mt-1 text-[#FF9800]">
                        $342K
                      </div>
                    </div>
                  </div>

                  {[
                    { sku: "FNDR-7X14-BLK", name: "Tandem fender · black", qty: 4, threshold: 10, low: true },
                    { sku: "AXL-3500-IDL",  name: "3,500 lb idler axle",   qty: 22, threshold: 8,  low: false },
                    { sku: "CPL-2516-CLS",  name: "2-5/16\" coupler",       qty: 0, threshold: 5, low: true },
                    { sku: "WHL-15-MOD-S",  name: "15\" mod wheel · silver", qty: 47, threshold: 12, low: false },
                  ].map((p) => (
                    <div
                      key={p.sku}
                      className="flex items-center justify-between py-1.5"
                    >
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-zinc-500">
                          {p.sku}
                        </div>
                        <div className="text-sm font-medium truncate">{p.name}</div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span
                          className={`font-mono font-bold text-sm ${
                            p.qty === 0
                              ? "text-red-600"
                              : p.low
                              ? "text-[#FF9800]"
                              : "text-emerald-600"
                          }`}
                        >
                          {p.qty}
                        </span>
                        {p.low && (
                          <span className="text-[9px] font-bold uppercase tracking-widest bg-[#FF9800] text-[#1A2332] px-1.5 py-0.5">
                            {p.qty === 0 ? "Out" : "Low"}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero bottom rule */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#FF9800]/40 to-transparent" />
      </section>

      {/* ===== Features ===== */}
      <section
        id="features"
        className="relative bg-white py-20 sm:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
              What's inside
            </div>
            <h2 className="font-display font-black tracking-tight mt-3 text-3xl sm:text-5xl text-[#0F172A]">
              Everything a parts manager <br className="hidden sm:block" />
              actually needs.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
              ezItemizer trades feature bloat for the things that move the needle on
              a shop floor: real counts, fast scanning, alerts that wake people up,
              and a paper trail that holds up in an audit.
            </p>
          </div>

          <div className="mt-14 sm:mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group bg-white p-7 sm:p-8 hover:bg-[#FFF8EE] transition-colors"
                >
                  <div className="h-11 w-11 bg-[#1A2332] text-[#FF9800] flex items-center justify-center group-hover:bg-[#FF9800] group-hover:text-[#1A2332] transition-colors">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <h3 className="font-display font-bold text-lg sm:text-xl mt-5 text-[#0F172A]">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-zinc-600 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Inline checklist strip */}
          <div className="mt-14 sm:mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
            {[
              "No-hardware barcode scanning",
              "Bcrypt + JWT auth with brute-force lockout",
              "Soft-delete with admin restore",
              "Inline cost editing for staff",
            ].map((item) => (
              <div key={item} className="flex items-start gap-2.5 text-sm text-zinc-700">
                <CheckCircle
                  size={18}
                  weight="fill"
                  className="text-[#FF9800] flex-shrink-0 mt-0.5"
                />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section
        id="how-it-works"
        className="relative bg-[#0F172A] text-white py-20 sm:py-28 overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#FF9800 1px, transparent 1px), linear-gradient(90deg, #FF9800 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
              How it works
            </div>
            <h2 className="font-display font-black tracking-tight mt-3 text-3xl sm:text-5xl">
              From box on the dock <br className="hidden sm:block" />
              to part on the trailer.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/10">
            {[
              {
                n: "01",
                title: "Scan it in.",
                body:
                  "Receiving a delivery? Open ezItemizer on a phone, scan the SKU, log the IN transaction with a unit cost. The audit trail starts there.",
              },
              {
                n: "02",
                title: "Pull what you need.",
                body:
                  "Building a trailer? Scan the part going onto the line, log the OUT, and the threshold engine watches your reorder points so you never run out mid-build.",
              },
              {
                n: "03",
                title: "Get warned, not surprised.",
                body:
                  "When stock hits the reorder line, every admin and warehouse user gets a push notification — even if they're nowhere near the office.",
              },
            ].map((s) => (
              <div
                key={s.n}
                data-testid={`how-step-${s.n}`}
                className="bg-[#0F172A] p-8 sm:p-10"
              >
                <div className="font-mono font-bold text-5xl sm:text-6xl text-[#FF9800] tracking-tight">
                  {s.n}
                </div>
                <h3 className="font-display font-bold text-xl sm:text-2xl mt-5">
                  {s.title}
                </h3>
                <p className="mt-3 text-base text-white/70 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 sm:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/15 pt-10">
            <div>
              <div className="font-display font-bold text-2xl sm:text-3xl">
                Ready when your floor is.
              </div>
              <p className="mt-2 text-white/65 text-sm sm:text-base">
                Sign in to start tracking parts, costs, and movements in minutes.
              </p>
            </div>
            <a
              href="#pricing"
              data-testid="footer-cta-signup"
              className="inline-flex items-center gap-2 bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] text-xs sm:text-sm font-bold uppercase tracking-widest px-6 py-4 transition-colors flex-shrink-0"
            >
              Start free trial
              <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="pricing" className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
              Pricing
            </div>
            <h2 className="font-display font-black tracking-tight mt-3 text-3xl sm:text-5xl text-[#0F172A]">
              Pick the plan that fits your floor.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
              Every plan includes a <span className="font-bold text-[#0F172A]">14-day free trial</span> — card required, but you won't be charged until day 15. Cancel anytime, no questions.
            </p>
          </div>

          <div className="mt-12 sm:mt-16 grid lg:grid-cols-3 gap-6 lg:gap-px lg:bg-zinc-200 max-w-6xl mx-auto">
            {plans.length === 0 && (
              <div className="lg:col-span-3 text-center py-16 text-zinc-500 font-mono text-sm" data-testid="pricing-loading">
                Loading plans…
              </div>
            )}
            {plans.map((p) => {
              const copy = TIER_COPY[p.tier] || { tagline: "", bullets: [] };
              const featured = p.tier === "team";
              const dollars = Math.floor((p.amount_cents || 0) / 100);
              return (
                <div
                  key={p.tier}
                  data-testid={`pricing-card-${p.tier}`}
                  className={`relative flex flex-col ${
                    featured
                      ? "bg-[#1A2332] text-white border-2 border-[#1A2332] lg:scale-[1.04] lg:z-10 shadow-2xl"
                      : "bg-white text-[#0F172A] border-2 border-[#1A2332] lg:border-0"
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF9800] text-[#1A2332] text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1 flex items-center gap-1.5">
                      <Star size={11} weight="fill" /> Most popular
                    </div>
                  )}

                  <div className={`px-7 sm:px-9 pt-10 pb-6 border-b ${featured ? "border-white/10" : "border-zinc-200"}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-[0.3em] ${featured ? "text-[#FF9800]" : "text-zinc-500"}`}>
                      {p.tier === "basic" ? "Solo" : p.tier === "team" ? "Growing shop" : "Operation"}
                    </div>
                    <div className={`font-display font-black text-2xl sm:text-3xl mt-2 ${featured ? "text-white" : "text-[#0F172A]"}`}>
                      {p.label}
                    </div>
                    <p className={`mt-1.5 text-sm ${featured ? "text-white/70" : "text-zinc-600"}`}>{copy.tagline}</p>
                    <div className="mt-5 flex items-end gap-2">
                      <span className={`font-display font-black text-5xl sm:text-6xl leading-none ${featured ? "text-white" : "text-[#0F172A]"}`}>
                        ${dollars}
                      </span>
                      <span className={`font-mono text-xs uppercase tracking-widest pb-2 ${featured ? "text-white/50" : "text-zinc-500"}`}>
                        /month
                      </span>
                    </div>
                    <p className={`mt-3 text-xs font-mono uppercase tracking-widest ${featured ? "text-white/50" : "text-zinc-500"}`}>
                      Up to {p.user_limit} {p.user_limit === 1 ? "user" : "users"} · 14-day trial
                    </p>
                  </div>

                  <ul className="px-7 sm:px-9 py-7 space-y-3 flex-1">
                    {copy.bullets.map((item) => (
                      <li
                        key={item}
                        className={`flex items-start gap-2.5 text-sm ${featured ? "text-white/85" : "text-zinc-700"}`}
                      >
                        <CheckCircle
                          size={18}
                          weight="fill"
                          className="text-[#FF9800] flex-shrink-0 mt-0.5"
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="px-7 sm:px-9 pb-7">
                    <a
                      href={p.payment_link}
                      data-testid={`pricing-cta-${p.tier}`}
                      target="_self"
                      rel="noopener"
                      className="w-full font-bold py-4 px-4 flex items-center justify-center gap-2 transition-colors active:scale-[0.99] bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332]"
                    >
                      Start 14-Day Free Trial
                      <ArrowRight size={16} weight="bold" />
                    </a>
                    <p className={`mt-3 text-center text-[11px] ${featured ? "text-white/50" : "text-zinc-500"}`}>
                      Secure checkout via Stripe · No charge today
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-xs text-zinc-500 font-mono uppercase tracking-widest">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF9800] hover:underline" data-testid="pricing-signin-link">
              Sign in →
            </Link>
          </p>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="bg-[#1A2332] text-white/60 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-2.5">
              <BrandMark size={32} className="rounded-md" />
              <span className="font-display font-bold text-white text-base">
                ezItemizer
              </span>
              <span className="text-xs uppercase tracking-widest text-white/40 ml-3 hidden sm:inline">
                Inventory Control
              </span>
            </div>
            <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link to="/privacy" data-testid="landing-footer-privacy" className="text-xs font-bold uppercase tracking-widest hover:text-[#FF9800] transition-colors">Privacy</Link>
              <Link to="/terms" data-testid="landing-footer-terms" className="text-xs font-bold uppercase tracking-widest hover:text-[#FF9800] transition-colors">Terms</Link>
              <Link to="/contact" data-testid="landing-footer-contact" className="text-xs font-bold uppercase tracking-widest hover:text-[#FF9800] transition-colors">Contact</Link>
              <Link to="/faq" data-testid="landing-footer-faq" className="text-xs font-bold uppercase tracking-widest hover:text-[#FF9800] transition-colors">FAQ</Link>
              <Link to="/help" data-testid="landing-footer-help" className="text-xs font-bold uppercase tracking-widest hover:text-[#FF9800] transition-colors">Help</Link>
            </nav>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs font-mono uppercase tracking-widest">
            <span>© {new Date().getFullYear()} ezItemizer · All rights reserved</span>
            <a
              href="https://gadgetguymedia.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="landing-footer-credit-ggm"
              className="hover:text-[#FF9800] transition-colors"
            >
              Website Designed &amp; Built by <span className="text-[#FF9800]">Gadget Guy Media</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
